/**
 * ShareModal — the main container component for board member management.
 *
 * This is a DialogContent component (not a full Dialog.Root) — the Dialog.Root
 * and Trigger will be provided by ShareButton (task 5.1).
 *
 * On open:
 * - Fetches GET /v1/boards/:boardId/members
 * - If admin, fetches GET /v1/boards/:boardId/invitations
 *
 * Renders:
 * - UserSearchInput (admin only, always visible at top)
 * - Sorted MemberList using sortMembersByRolePriority
 * - Pending additions (staged, not yet sent to API) with "New" badge
 * - PendingInvitationsSection (admin only, hidden when empty)
 * - Save button when there are pending additions
 *
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 3.9, 5.1, 5.2, 5.3, 5.4, 5.5,
 *            8.1, 8.4, 8.5, 10.1, 10.6, 10.7, 10.8
 */

import * as React from 'react';
import { DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import * as api from '../../services/api-client';
import { isClientError } from '../../services/api-client';
import { ShareModalSearch } from './ShareModalSearch';
import { ShareModalMemberRow } from './ShareModalMemberRow';
import { VisibilitySelector, VisibilityMode } from '../VisibilitySelector';
import {
  sortMembersByRolePriority,
  filterPendingInvitations,
  buildInvitationPayload,
} from './sharing-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import type {
  BoardMemberItem,
  BoardRole,
  PendingInvitationItem,
  SelectedUser,
} from './sharing-types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ShareModalProps {
  readonly boardId: string;
  readonly currentUserId: string;
  readonly currentUserRole: BoardRole;
  readonly onMembershipChanged: () => void;
  readonly currentVisibility?: string;
}

// ---------------------------------------------------------------------------
// Batch invite types
// ---------------------------------------------------------------------------

export interface PendingAddition {
  user: SelectedUser;
  role: BoardRole;
}

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface MembersResponse {
  data: BoardMemberItem[];
}

interface InvitationsResponse {
  data: PendingInvitationItem[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_BADGE_STYLES: Record<BoardRole, string> = {
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  normal: 'bg-green-100 text-green-800 border-green-200',
  'read-only': 'bg-gray-100 text-gray-700 border-gray-200',
};

const ROLE_LABELS: Record<BoardRole, string> = {
  admin: 'Admin',
  normal: 'Normal',
  'read-only': 'Read-only',
};

/**
 * User-facing message for the EXTERNAL_INVITE_CAP_REACHED backend code. Rendered
 * identically for known and unknown emails so the limit never reveals account
 * existence (non-enumeration — design Decision 8).
 */
const EXTERNAL_INVITE_CAP_MESSAGE =
  'This board has reached its pending invitation limit. Try again once some invites are accepted or expire.';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShareModal({
  boardId,
  currentUserId,
  currentUserRole,
  onMembershipChanged,
  currentVisibility,
}: ShareModalProps): React.ReactElement {
  // ---- State ----
  const [members, setMembers] = React.useState<BoardMemberItem[]>([]);
  const [pendingInvitations, setPendingInvitations] = React.useState<PendingInvitationItem[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = React.useState(true);
  const [membersError, setMembersError] = React.useState<string | null>(null);
  const [pendingAdditions, setPendingAdditions] = React.useState<PendingAddition[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = React.useState<string | null>(null);
  const [roleChangeError, setRoleChangeError] = React.useState<string | null>(null);
  const [revokeError, setRevokeError] = React.useState<string | null>(null);
  const [removeError, setRemoveError] = React.useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = React.useState<string | null>(null);

  // currentUserId reserved for future self-removal guard
  void currentUserId;

  const isAdmin = currentUserRole === 'admin';
  const titleId = 'share-modal-title';

  // ---- Data fetching ----

  const fetchMembers = React.useCallback(async () => {
    setIsLoadingMembers(true);
    setMembersError(null);
    try {
      const response = await api.get<MembersResponse>(`/v1/boards/${boardId}/members`);
      setMembers((response.data ?? []).map(m => ({
        ...m,
        displayName: m.displayName || m.email || m.userId || '',
      })));
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to load members';
      setMembersError(message);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [boardId]);

  const fetchInvitations = React.useCallback(async () => {
    try {
      const response = await api.get<InvitationsResponse>(`/v1/boards/${boardId}/invitations`);
      setPendingInvitations(filterPendingInvitations(response.data ?? []));
    } catch {
      // Silently hide pending section on failure (non-critical)
    }
  }, [boardId]);

  React.useEffect(() => {
    fetchMembers();
    if (isAdmin) {
      fetchInvitations();
    }
  }, [fetchMembers, fetchInvitations, isAdmin]);

  // ---- Derived data ----

  const sortedMembers = sortMembersByRolePriority(members);
  const adminCount = members.filter((m) => m.role === 'admin').length;
  const excludeUserIds = [
    ...members.map((m) => m.userId),
    ...pendingAdditions
      .filter((p) => 'userId' in p.user)
      .map((p) => (p.user as { userId: string }).userId),
  ];
  const filteredPendingInvitations = filterPendingInvitations(pendingInvitations);

  // ---- Batch invite handlers ----

  const handleUserSelect = React.useCallback((user: SelectedUser) => {
    setPendingAdditions(prev => [...prev, { user, role: 'normal' }]);
    setSaveError(null);
    setSaveSuccess(null);
  }, []);

  const handleRemovePending = React.useCallback((index: number) => {
    setPendingAdditions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handlePendingRoleChange = React.useCallback((index: number, newRole: BoardRole) => {
    setPendingAdditions(prev =>
      prev.map((item, i) => (i === index ? { ...item, role: newRole } : item)),
    );
  }, []);

  const handleSaveAll = React.useCallback(async () => {
    if (pendingAdditions.length === 0) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    // Capture the staged count/label before clearing, so the success message can
    // name the single invitee (or summarize a batch).
    const attempted = pendingAdditions.length;
    const firstLabel = pendingAdditions[0]
      ? ('userId' in pendingAdditions[0].user
          ? pendingAdditions[0].user.email
          : pendingAdditions[0].user.email)
      : '';

    let successCount = 0;
    const errors: string[] = [];

    for (const addition of pendingAdditions) {
      try {
        const payload = buildInvitationPayload(addition.user, addition.role);
        await api.post(`/v1/boards/${boardId}/invitations`, payload);
        successCount++;
      } catch (error: unknown) {
        const userLabel = 'userId' in addition.user
          ? addition.user.displayName
          : addition.user.email;
        if (isClientError(error) && error.type === 'api') {
          // ALREADY_MEMBER / INVITATION_PENDING are retained for known-user /
          // legacy paths. The old USER_NOT_FOUND branch is gone: an email for
          // someone not on Flow now returns a uniform pending 201 (success),
          // so there is no "isn't on Flow yet" error to surface.
          if (error.code === 'ALREADY_MEMBER') {
            errors.push(`${userLabel}: already a member`);
          } else if (error.code === 'INVITATION_PENDING') {
            errors.push(`${userLabel}: invitation already pending`);
          } else if (error.code === 'EXTERNAL_INVITE_CAP_REACHED') {
            // The board is at its external-invite ceiling. The backend returns
            // the same 409/EXTERNAL_INVITE_CAP_REACHED for known and unknown
            // emails (non-enumeration), so the friendly limit copy is rendered
            // here, identically, regardless of whether the email is a Flow user.
            errors.push(`${userLabel}: ${EXTERNAL_INVITE_CAP_MESSAGE}`);
          } else {
            errors.push(`${userLabel}: ${error.message}`);
          }
        } else {
          errors.push(`${userLabel}: failed to send invitation`);
        }
      }
    }

    setPendingAdditions([]);
    await fetchMembers();
    if (isAdmin) await fetchInvitations();
    onMembershipChanged();

    if (errors.length > 0) {
      const total = successCount + errors.length;
      const summary = successCount > 0
        ? `${successCount} of ${total} invitation${total > 1 ? 's' : ''} sent successfully. Errors: ${errors.join('; ')}`
        : `Failed to send invitations: ${errors.join('; ')}`;
      setSaveError(summary);
    } else if (successCount > 0) {
      // Fully successful: announce via a polite live region. Single invite names
      // the invitee; a batch summarizes the count. A uniform pending 201 for an
      // email that isn't on Flow yet counts as success here (Decision 8).
      setSaveSuccess(
        attempted === 1
          ? `Invitation sent to ${firstLabel}`
          : `${successCount} invitation${successCount > 1 ? 's' : ''} sent`,
      );
    }

    setIsSaving(false);
  }, [pendingAdditions, boardId, isAdmin, fetchMembers, fetchInvitations, onMembershipChanged]);

  // ---- Existing member handlers ----

  const handleRoleChange = React.useCallback(async (userId: string, newRole: BoardRole) => {
    // Optimistic update
    const previousMembers = members;
    setMembers((prev) =>
      prev.map((m) => (m.userId === userId ? { ...m, role: newRole } : m)),
    );
    setRoleChangeError(null);
    try {
      await api.put(`/v1/boards/${boardId}/members/${userId}/role`, { role: newRole });
      onMembershipChanged();
    } catch (error: unknown) {
      // Revert on failure
      setMembers(previousMembers);
      if (isClientError(error) && error.type === 'api' && error.code === 'LAST_ADMIN') {
        setRoleChangeError('Cannot change role: board must retain at least one admin.');
      } else {
        const message =
          isClientError(error) && error.type === 'api'
            ? error.message
            : 'Failed to change role';
        setRoleChangeError(message);
      }
    }
  }, [members, boardId, onMembershipChanged]);

  const handleRemove = React.useCallback((userId: string) => {
    setRemovingMemberId(userId);
  }, []);

  const handleConfirmRemove = React.useCallback(async () => {
    if (!removingMemberId) return;
    const userId = removingMemberId;
    setRemovingMemberId(null);
    setRemoveError(null);
    try {
      await api.del(`/v1/boards/${boardId}/members/${userId}`);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
      onMembershipChanged();
    } catch (error: unknown) {
      const message =
        isClientError(error) && error.type === 'api'
          ? error.message
          : 'Failed to remove member';
      setRemoveError(message);
    }
  }, [removingMemberId, boardId, onMembershipChanged]);

  const handleCancelRemove = React.useCallback(() => {
    setRemovingMemberId(null);
  }, []);

  const handleRevoke = React.useCallback(async (invitationId: string) => {
    setRevokeError(null);
    try {
      await api.del(`/v1/boards/${boardId}/invitations/${invitationId}`);
      setPendingInvitations((prev) => prev.filter((inv) => inv.invitationId !== invitationId));
    } catch (error: unknown) {
      const message =
        isClientError(error) && error.type === 'api'
          ? error.message
          : 'Failed to revoke invitation';
      setRevokeError(message);
    }
  }, [boardId]);

  // ---- Render ----

  return (
    <DialogContent
      className="max-w-[480px] overflow-visible"
      aria-labelledby={titleId}
      aria-modal="true"
    >
      <DialogHeader>
        <DialogTitle id={titleId}>Share board</DialogTitle>
      </DialogHeader>

      {/* Visibility selector (admin only) */}
      {isAdmin && (
        <div className="mb-3 pb-3 border-b border-border">
          <VisibilitySelector
            currentVisibility={
              (currentVisibility === 'public-read' || currentVisibility === 'public-edit')
                ? currentVisibility
                : currentVisibility === 'public' ? 'public-read' : 'private'
            }
            onVisibilityChange={async (newVisibility: VisibilityMode) => {
              await api.put(`/v1/boards/${boardId}`, { visibility: newVisibility });
              onMembershipChanged();
            }}
          />
        </div>
      )}

      {/* Search input (admin only, always visible) */}
      {isAdmin && (
        <div className="space-y-3">
          <ShareModalSearch
            excludeUserIds={excludeUserIds}
            onSelect={handleUserSelect}
          />
        </div>
      )}

      {/* Member list + pending additions */}
      <div className="mt-2 max-h-[320px] overflow-y-auto" role="list" aria-label="Board members">
        {isLoadingMembers && <MemberListSkeleton />}

        {membersError && (
          <div className="flex flex-col items-center gap-2 py-4" role="alert">
            <p className="text-sm text-destructive">{membersError}</p>
            <button
              type="button"
              onClick={fetchMembers}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoadingMembers && !membersError && sortedMembers.map((member) => (
          <ShareModalMemberRow
            key={member.userId}
            member={member}
            isAdmin={isAdmin}
            isLastAdmin={member.role === 'admin' && adminCount === 1}
            onRoleChange={handleRoleChange}
            onRemove={handleRemove}
          />
        ))}

        {/* Pending additions (staged, not yet saved) */}
        {pendingAdditions.map((addition, index) => (
          <PendingAdditionRow
            key={`pending-${index}`}
            addition={addition}
            index={index}
            onRoleChange={handlePendingRoleChange}
            onRemove={handleRemovePending}
          />
        ))}
      </div>

      {/* Pending invitations (admin only, hidden when empty) */}
      {isAdmin && filteredPendingInvitations.length > 0 && (
        <PendingInvitationsSection
          invitations={filteredPendingInvitations}
          onRevoke={handleRevoke}
        />
      )}

      {/* Role change error message */}
      {roleChangeError && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {roleChangeError}
        </p>
      )}

      {/* Remove member error message */}
      {removeError && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {removeError}
        </p>
      )}

      {/* Revoke invitation error message */}
      {revokeError && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {revokeError}
        </p>
      )}

      {/* Save error / summary message */}
      {saveError && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {saveError}
        </p>
      )}

      {/* Save success message — polite live region, not conveyed by color alone */}
      {saveSuccess && (
        <p className="mt-2 text-sm text-green-700" role="status" aria-live="polite">
          {saveSuccess}
        </p>
      )}

      {/* Save button — only shown when there are pending additions */}
      {pendingAdditions.length > 0 && (
        <div className="mt-4 flex justify-end border-t pt-3">
          <button
            type="button"
            onClick={handleSaveAll}
            disabled={isSaving}
            className={cn(
              'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
              'hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {isSaving
              ? 'Saving…'
              : `Add ${pendingAdditions.length} member${pendingAdditions.length > 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Remove member confirmation dialog */}
      <AlertDialog open={removingMemberId !== null} onOpenChange={(open) => { if (!open) handleCancelRemove(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {members.find((m) => m.userId === removingMemberId)?.displayName ?? 'this member'} from this board? They will lose access and be unassigned from all cards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelRemove}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// PendingAdditionRow — renders a staged (unsaved) addition in the member list
// ---------------------------------------------------------------------------

interface PendingAdditionRowProps {
  readonly addition: PendingAddition;
  readonly index: number;
  readonly onRoleChange: (index: number, newRole: BoardRole) => void;
  readonly onRemove: (index: number) => void;
}

function PendingAdditionRow({
  addition,
  index,
  onRoleChange,
  onRemove,
}: PendingAdditionRowProps): React.ReactElement {
  const displayName = 'userId' in addition.user
    ? addition.user.displayName
    : addition.user.email;
  const email = 'userId' in addition.user
    ? addition.user.email
    : addition.user.email;

  return (
    <div className="flex items-center gap-3 rounded-md border border-dashed border-primary/30 bg-primary/5 px-2 py-2">
      {/* Placeholder avatar */}
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary"
        aria-hidden="true"
      >
        {displayName.charAt(0).toUpperCase()}
      </div>

      {/* Name and email */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">
            {displayName}
          </p>
          <Badge className="shrink-0 bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0">
            New
          </Badge>
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {email}
        </p>
      </div>

      {/* Role selector */}
      <Select
        value={addition.role}
        onValueChange={(value) => onRoleChange(index, value as BoardRole)}
      >
        <SelectTrigger
          className="h-8 w-[120px] shrink-0 text-xs"
          aria-label={`Role for ${displayName}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="admin">Admin</SelectItem>
          <SelectItem value="normal">Normal</SelectItem>
          <SelectItem value="read-only">Read-only</SelectItem>
        </SelectContent>
      </Select>

      {/* Remove from staging button */}
      <button
        type="button"
        aria-label={`Remove ${displayName} from pending additions`}
        onClick={() => onRemove(index)}
        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <XIcon />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PendingInvitationsSection
// ---------------------------------------------------------------------------

interface PendingInvitationsSectionProps {
  readonly invitations: readonly PendingInvitationItem[];
  readonly onRevoke: (invitationId: string) => void;
}

function PendingInvitationsSection({
  invitations,
  onRevoke,
}: PendingInvitationsSectionProps): React.ReactElement {
  return (
    <div className="mt-4 border-t pt-4">
      <h3 className="mb-2 text-sm font-medium text-muted-foreground">
        Pending invitations
      </h3>
      <div role="list" aria-label="Pending invitations">
        {invitations.map((invitation) => (
          <div
            key={invitation.invitationId}
            className="flex items-center gap-3 px-2 py-2"
            role="listitem"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-foreground">
                {invitation.inviteeEmail}
              </p>
              {/* Text affordance (not color-only) distinguishing an email invite. */}
              <p className="truncate text-xs text-muted-foreground">
                Pending — invited by email
              </p>
            </div>
            <Badge className={cn('shrink-0', ROLE_BADGE_STYLES[invitation.role])}>
              {ROLE_LABELS[invitation.role]}
            </Badge>
            <button
              type="button"
              onClick={() => onRevoke(invitation.invitationId)}
              className="shrink-0 rounded-md px-2 py-1 text-xs text-destructive hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label={`Revoke invitation for ${invitation.inviteeEmail}`}
            >
              Revoke
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MemberListSkeleton — loading placeholder
// ---------------------------------------------------------------------------

function MemberListSkeleton(): React.ReactElement {
  return (
    <div className="space-y-3 py-2" aria-label="Loading members" role="status">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex animate-pulse items-center gap-3 px-2">
          <div className="h-8 w-8 rounded-full bg-muted" />
          <div className="flex-1 space-y-1">
            <div className="h-4 w-32 rounded bg-muted" />
            <div className="h-3 w-48 rounded bg-muted" />
          </div>
          <div className="h-6 w-20 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// XIcon — inline SVG for the remove-from-staging button
// ---------------------------------------------------------------------------

function XIcon(): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
